import { Suspense, lazy } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { HubLayout } from '@/components/layout/HubLayout';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { ROUTES } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { McpSetupPage } from '@/pages/resources/McpSetupPage';
import { DEFAULT_MCP_HUB_SECTION, MCP_HUB_SECTIONS } from './mcp-hub-config';

const McpServersPage = lazy(() =>
  import('@reflex/plugin-mcp/web/McpServersPage').then((mod) => ({ default: mod.McpServersPage })),
);

function McpHubContent({ section }: { section: string }) {
  if (section === 'setup') {
    return <McpSetupPage embedded />;
  }

  return (
    <Suspense
      fallback={
        <AsyncContent isLoading isError={false} loadingMessage="Loading MCP servers…">
          {null}
        </AsyncContent>
      }
    >
      <McpServersPage embedded />
    </Suspense>
  );
}

/**
 * Host-owned MCP hub: MCP Servers and client setup in one left-rail layout.
 */
export function McpHubPage() {
  const slug = useActiveOrgSlug();
  const { section: sectionParam } = useParams();
  const section = sectionParam === 'setup' ? 'setup' : 'servers';

  if (!slug) {
    return <Navigate to={ROUTES.settings} replace />;
  }

  const orgRoutes = ROUTES.org(slug);
  const activeSection = MCP_HUB_SECTIONS.find((s) => s.id === section);

  if (!activeSection) {
    return <Navigate to={orgRoutes.mcpHubSection(DEFAULT_MCP_HUB_SECTION)} replace />;
  }

  return (
    <HubLayout
      title="MCP"
      description="Register MCP servers and connect external clients to Reflex."
      sections={MCP_HUB_SECTIONS}
      activeSectionId={section}
      sectionHref={(item) => orgRoutes.mcpHubSection(item.id)}
      navLabel="MCP sections"
    >
      <McpHubContent section={section} />
    </HubLayout>
  );
}
